/**
 * Buy 2 Get 1 / Buy 3 Get 2 — free pack picker for the Rebuy cart drawer.
 *
 * Offers come from config.tiers, e.g. [{paid: 2, free: 1}, {paid: 3, free: 2}]:
 * the cart earns the free packs of the highest tier its paid, one-time packs
 * reach. Free packs are gift lines carrying the property Gift: "Buy 2 Get 1"
 * (one source for both tiers, so the shim styles them and GiftGuard protects
 * them); the Shopify Buy X Get Y discounts are what actually make them free.
 *
 * Gift lines arrive three ways, and all end up looking the same:
 *   - picked here, from the drawer;
 *   - added by the PDP buy box (assets/thh-product-main.js) with the property;
 *   - a regular line the discount freed: converted into a gift line here.
 *
 * Config comes from snippets/b2g1-cart-config.liquid (window.THHB2G1Config).
 * New gift lines go through window.THHPickACard.reconcileGifts so there is one
 * lock, one dedupe and one guard for every gift source on this store — see
 * assets/pick-a-card-cart.js. reconcileGifts holds one line per variant, so an
 * extra unit of a flavour already gifted, a trim and a repin post to the cart
 * directly; the layer observes those requests like any other cart write.
 * 'Buy 2 Get 1' must be listed in GIFT_SOURCES there and in
 * snippets/pick-a-card-cart-shim.liquid.
 *
 * Drawer markup it builds into — the theme's THH drawer
 * (snippets/thh-cart-drawer-items.liquid, thh-cart-line.liquid) or, while
 * Rebuy Smart Cart is still on, Rebuy's:
 *   [data-thh-cart-lines]              / .rebuy-cart__flyout-item's <ul>  (anchor)
 *   .thh-line[data-gift-source]        / .rebuy-cart__flyout-item.property-value-buy-2-get-1
 *   [data-thh-discount-line]           / .rebuy-cart__flyout-item-discount-line (relabelled)
 * The THH drawer re-renders on every cart change, which removes the block;
 * the MutationObserver puts it straight back.
 */
(function () {
  if (window.THHB2G1) return;

  var SOURCE = 'Buy 2 Get 1';
  var BLOCK_CLASS = 'b2g1-picker';
  var REPIN_LIMIT = 2;
  var CONVERT_LIMIT = 2;

  var config = window.THHB2G1Config || {};
  var products = config.products || {};      // product_id -> { handle, title, image, variants: { "15": {...}, "30": {...} } }
  // Eligible for the offer but not offered as a tile: Daily Dose Shots, whose
  // flavours are variants rather than products of their own.
  var countOnly = (config.countProducts || []).map(String);
  var tiers = (config.tiers && config.tiers.length
    ? config.tiers
    : [{ paid: config.packsPerGift || 2, free: config.giftsPerOrder || 1 }])
    .map(function (t) { return { paid: Number(t.paid) || 0, free: Number(t.free) || 0 }; })
    .filter(function (t) { return t.paid > 0 && t.free > 0; })
    .sort(function (a, b) { return a.paid - b.paid; });
  var code = config.discountCode || '';
  // Every discount that frees our packs, by the title Shopify reports.
  var titles = [code, config.discountTitle].concat(config.discountTitles || []).filter(Boolean);
  // Only these are relabelled: the code is not a name a customer should read.
  var relabelTitles = [code, config.discountTitle].filter(Boolean);
  var copy = config.copy || {};

  var cart = null;
  var busy = false;
  // Change mode: the variant ids picked so far, or null. The current gifts stay
  // in the cart until the new picks replace them in one write — removing them
  // first lets the discount land on the customer's paid packs instead.
  var changing = null;
  var repins = 0;
  var converts = 0;
  var codeRetries = 0;
  var lastSignature = '';

  function log(message, error) {
    if (window.console && console.warn) console.warn('[b2g1] ' + message, error || '');
  }

  function layer() { return window.THHPickACard || null; }

  function fill(template, values) {
    return String(template || '').replace(/%(\w+)%/g, function (match, key) {
      return values[key] != null ? values[key] : match;
    });
  }

  // ---- cart reading ----

  function packSize(line) {
    var options = line.options_with_values || [];
    for (var i = 0; i < options.length; i++) {
      var match = /(\d+)\s*Pack/i.exec(options[i].value || '');
      if (match) return Number(match[1]);
    }
    var fromTitle = /(\d+)\s*Pack/i.exec(line.variant_title || line.title || '');
    return fromTitle ? Number(fromTitle[1]) : 0;
  }

  function isGift(line) {
    return !!(line.properties && line.properties.Gift === SOURCE);
  }

  function isAnyGift(line) {
    return !!(line.properties && (line.properties.Gift || line.properties._auto_gift));
  }

  function isEligibleProduct(productId) {
    var id = String(productId);
    return !!products[id] || countOnly.indexOf(id) !== -1;
  }

  // Paid, one-time, one of ours. Subscription lines never count: the offer is
  // one-time purchase only on the discount, so counting them here would show a
  // picker for a cart the discount will not honour.
  function isEligiblePaid(line) {
    if (!isEligibleProduct(line.product_id)) return false;
    if (isAnyGift(line)) return false;
    if (line.selling_plan_allocation) return false;
    return line.original_line_price > 0;
  }

  // Money our discounts took off a line, read from the allocations Shopify
  // reports on /cart.js. A qty-2 line with one free unit has a non-zero
  // final_line_price, so the price alone cannot say whether it was freed.
  function discountOn(line) {
    var allocations = line.line_level_discount_allocations || [];
    var total = 0;
    for (var i = 0; i < allocations.length; i++) {
      var application = allocations[i].discount_application || {};
      if (titles.indexOf(application.title) !== -1) total += Number(allocations[i].amount) || 0;
    }
    return total;
  }

  // Units of a line our discounts made free.
  function freedUnits(line) {
    if (line.original_line_price > 0 && line.final_line_price === 0) return line.quantity;
    var unit = line.original_price || (line.quantity ? line.original_line_price / line.quantity : 0);
    return unit > 0 ? Math.min(line.quantity, Math.round(discountOn(line) / unit)) : 0;
  }

  function sumUnits(lines, count) {
    return lines.reduce(function (sum, line) { return sum + (count ? count(line) : line.quantity); }, 0);
  }

  // Free packs the highest reached tier gives, and the next tier up.
  function freeFor(units) {
    var free = 0;
    tiers.forEach(function (t) { if (units >= t.paid) free = Math.max(free, t.free); });
    return free;
  }

  function nextTier(units) {
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].paid > units && tiers[i].free > freeFor(units)) return tiers[i];
    }
    return null;
  }

  function read(currentCart) {
    var items = (currentCart && currentCart.items) || [];
    var paid = items.filter(isEligiblePaid);
    var units = sumUnits(paid);
    var gifts = items.filter(isGift);
    var giftUnits = sumUnits(gifts);
    var freedPaid = paid.filter(function (line) { return freedUnits(line) > 0; });
    var freedPaidUnits = sumUnits(paid, freedUnits);
    var wanted = freeFor(units);
    var sizes = paid.map(packSize).filter(Boolean);
    return {
      paid: paid,
      units: units,
      // Free packs the cart has earned.
      wanted: wanted,
      gifts: gifts,
      giftUnits: giftUnits,
      freedGiftUnits: sumUnits(gifts, freedUnits),
      // A paid pack the discount already made free uses up a slot until it is
      // converted into a gift line: offering another would be charged.
      freed: freedPaid,
      freedPaidUnits: freedPaidUnits,
      missing: Math.max(0, wanted - giftUnits - freedPaidUnits),
      next: nextTier(units),
      minSize: sizes.length ? Math.min.apply(null, sizes) : 0
    };
  }

  function variantFor(productId, size) {
    var product = products[String(productId)];
    if (!product) return null;
    var variants = product.variants || {};
    return variants[String(size)] || null;
  }

  // ---- cart writes ----

  function giftProperties() { return { Gift: SOURCE }; }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  function applyCode() {
    if (!code) return Promise.resolve(true);
    // /discount/CODE 302-redirects; /cart.js is a cheap destination.
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok; })
      .catch(function () { return false; });
  }

  // Same signal the shared layer sends after its own writes, plus a direct
  // refresh of whichever drawer is live (assets/thh-cart-drawer.js).
  function announce() {
    if (window.THHCart) { window.THHCart.refresh(); return; }
    document.dispatchEvent(new CustomEvent('theme:update:cart', { bubbles: true }));
    try {
      if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') window.Rebuy.Cart.fetchCart();
    } catch (e) {}
  }

  // After our own write, read the cart ourselves. The shared layer holds its
  // lock until the theme's cart cycle ends and drops change signals meanwhile,
  // so waiting for onCartChange left the picker showing the cart from before
  // the gift landed — and the next pick replaced that gift instead of adding.
  function run(work) {
    if (busy) return Promise.resolve();
    busy = true;
    render();
    return Promise.resolve()
      .then(work)
      .catch(function (e) { log('could not update the free packs', e); })
      .then(function () {
        busy = false;
        announce();
        var l = layer();
        return l ? l.readCart().then(function (fresh) { if (fresh) onCart(fresh); render(); }) : render();
      });
  }

  // Add one free pack of this variant: through the shared layer when it is a
  // new gift line, straight to the cart when that flavour is already gifted.
  // Reads the cart first: the gifts already in it must stay, and a stale copy
  // would have reconcileGifts remove them as "no longer wanted".
  function addGift(variantId) {
    var l = layer();
    if (!l) return Promise.resolve();
    return applyCode().then(function () { return l.readCart(); }).then(function (fresh) {
      if (fresh) cart = fresh;
      var gifts = cart ? read(cart).gifts : [];
      var state = cart ? read(cart) : null;
      // Already holding every free pack the cart earns: nothing to add.
      if (state && state.giftUnits + state.freedPaidUnits >= state.wanted) return null;
      var existing = gifts.filter(function (g) { return String(g.variant_id) === String(variantId); })[0];
      if (existing) {
        return post('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1, properties: giftProperties() }] });
      }
      return l.reconcileGifts(SOURCE, gifts.map(function (g) { return g.variant_id; }).concat([variantId]));
    });
  }

  function clearGifts() {
    var l = layer();
    return l ? l.reconcileGifts(SOURCE, []) : Promise.resolve();
  }

  // Swap every gift line for these picks. Runs inside run(), whose busy flag
  // keeps reconcile from reacting to the moment between the two writes.
  function replaceGifts(ids) {
    var zero = {};
    (cart ? read(cart).gifts : []).forEach(function (g) { zero[g.key] = 0; });
    return applyCode()
      .then(function () { return Object.keys(zero).length ? post('/cart/update.js', { updates: zero }) : true; })
      .then(function () { return reAdd(ids); });
  }

  // Re-add gift units as the newest lines, which is where the discount lands.
  function reAdd(units) {
    if (!units.length) return Promise.resolve();
    var byId = {};
    units.forEach(function (id) { byId[id] = (byId[id] || 0) + 1; });
    return post('/cart/add.js', {
      items: Object.keys(byId).map(function (id) {
        return { id: Number(id), quantity: byId[id], properties: giftProperties() };
      })
    });
  }

  function giftUnitIds(gifts) {
    var ids = [];
    gifts.forEach(function (g) { for (var i = 0; i < g.quantity; i++) ids.push(g.variant_id); });
    return ids;
  }

  /**
   * Keep the gift lines consistent with the cart after every change.
   *
   * - no tier reached: remove the gifts.
   * - more gift units than the tier gives: trim the newest extras.
   * - a gift bigger than the smallest paid pack: swap it for the same flavour
   *   at the smaller size, which is the one Shopify frees.
   * - the discount freed a regular line and the cart is short of gifts: turn
   *   those units into gift lines, so a free pack always looks like one.
   * - the discount landed on a paid line instead of a gift: re-add the gifts
   *   so they are the newest lines. Capped, so two writers never ping-pong.
   */
  function reconcile(state) {
    if (!layer() || busy) return;

    if (!state.wanted) {
      if (state.gifts.length) run(clearGifts);
      return;
    }

    if (state.giftUnits > state.wanted) {
      var keep = state.wanted;
      var updates = {};
      state.gifts.forEach(function (g) {
        var q = Math.min(g.quantity, keep);
        keep -= q;
        if (q !== g.quantity) updates[g.key] = q;
      });
      run(function () { return post('/cart/update.js', { updates: updates }); });
      return;
    }

    var oversized = state.minSize ? state.gifts.filter(function (g) {
      return packSize(g) > state.minSize && variantFor(g.product_id, state.minSize);
    }) : [];
    if (oversized.length) {
      var swaps = [];
      var drop = {};
      oversized.forEach(function (g) {
        var smaller = variantFor(g.product_id, state.minSize);
        if (!smaller.available) return;
        drop[g.key] = 0;
        for (var i = 0; i < g.quantity; i++) swaps.push(smaller.id);
      });
      if (swaps.length) {
        run(function () { return post('/cart/update.js', { updates: drop }).then(function () { return reAdd(swaps); }); });
      }
      return;
    }

    // Convert only units whose move to a gift line keeps the cart on the same
    // offer: 5 regular packs become 3 + 2 free, but 3 stay 3 paid, which earn
    // 2 free rather than dropping to 2 paid + 1.
    var convertible = 0;
    while (convertible < state.freedPaidUnits &&
      freeFor(state.units - convertible - 1) >= state.wanted) convertible += 1;

    if (convertible > 0 && state.giftUnits < state.wanted && converts < CONVERT_LIMIT) {
      converts += 1;
      var room = Math.min(state.wanted - state.giftUnits, convertible);
      var lower = {};
      var gifted = [];
      state.freed.forEach(function (line) {
        var n = Math.min(freedUnits(line), room);
        if (n <= 0) return;
        room -= n;
        lower[line.key] = line.quantity - n;
        for (var i = 0; i < n; i++) gifted.push(line.variant_id);
      });
      if (gifted.length) {
        run(function () { return post('/cart/update.js', { updates: lower }).then(function () { return reAdd(gifted); }); });
      }
      return;
    }

    if (!state.gifts.length) return;

    // Nothing in the cart is free: the discount never landed (code cookie
    // lost, for instance). Re-apply it once and ask for a fresh read.
    if (!state.freedGiftUnits && !state.freedPaidUnits && code && codeRetries < 1) {
      codeRetries += 1;
      applyCode().then(function () {
        document.dispatchEvent(new CustomEvent('theme:update:cart', { bubbles: true }));
      });
      return;
    }

    if (state.freedGiftUnits < state.giftUnits && state.freedPaidUnits > 0 && repins < REPIN_LIMIT) {
      repins += 1;
      var ids = giftUnitIds(state.gifts);
      var zero = {};
      state.gifts.forEach(function (g) { zero[g.key] = 0; });
      run(function () { return post('/cart/update.js', { updates: zero }).then(function () { return reAdd(ids); }); });
    }
  }

  // ---- drawer UI ----

  function money(cents) {
    var amount = (Number(cents) || 0) / 100;
    return '$' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
  }

  var DRAWERS = '.thh-cart, .rebuy-cart__flyout';

  // The live drawer: Rebuy's while Smart Cart is on (the THH drawer still
  // renders then, hidden), the THH drawer once it is off.
  function anchor() {
    var rebuyLive = window.THHCart ? window.THHCart.rebuyActive() : true;
    var item = rebuyLive && document.querySelector('.rebuy-cart__flyout .rebuy-cart__flyout-item');
    if (item) return item.closest('ul');
    var lines = document.querySelector('.thh-cart [data-thh-cart-lines]');
    return lines && lines.children.length ? lines : null;
  }

  function inDrawer(selector) {
    return document.querySelector(DRAWERS.split(', ').map(function (d) { return d + ' ' + selector; }).join(', '));
  }

  function block() {
    var list = anchor();
    if (!list) return null;
    var existing = list.parentNode.querySelector('.' + BLOCK_CLASS);
    if (existing) return existing;
    var el = document.createElement('div');
    el.className = BLOCK_CLASS;
    el.setAttribute('data-b2g1', '');
    list.insertAdjacentElement('afterend', el);
    return el;
  }

  function removeBlock() {
    var el = inDrawer('.' + BLOCK_CLASS);
    if (el) el.parentNode.removeChild(el);
  }

  function paragraph(className, text) {
    var p = document.createElement('p');
    p.className = BLOCK_CLASS + '__' + className;
    p.textContent = text;
    return p;
  }

  function tile(productId, product, size) {
    var variant = variantFor(productId, size);
    var button = document.createElement('button');
    button.type = 'button';
    button.className = BLOCK_CLASS + '__tile';
    button.setAttribute('data-variant-id', variant ? variant.id : '');
    if (!variant || !variant.available) {
      button.disabled = true;
      button.classList.add('is-sold-out');
    }
    var img = document.createElement('img');
    img.src = product.image || '';
    img.alt = '';
    img.loading = 'lazy';
    img.width = 64; img.height = 64;
    var name = document.createElement('span');
    name.className = BLOCK_CLASS + '__tile-name';
    name.textContent = product.title;
    var price = document.createElement('span');
    price.className = BLOCK_CLASS + '__tile-price';
    if (variant && variant.available) {
      var was = document.createElement('s');
      was.textContent = money(variant.price);
      price.appendChild(was);
      price.appendChild(document.createTextNode(' ' + (copy.free || 'FREE')));
    } else {
      price.textContent = copy.soldOut || 'Sold out';
    }
    button.appendChild(img);
    button.appendChild(name);
    button.appendChild(price);
    return button;
  }

  function lineName(line) {
    var product = products[String(line.product_id)] || {};
    var name = product.title;
    if (!name) {
      // Daily Dose Shots: the flavour is the variant's first option.
      var first = (line.options_with_values || [])[0];
      name = first && !/Pack/i.test(first.value) ? first.value : line.product_title;
    }
    return name + ' ' + (packSize(line) || '') + ' Pack';
  }

  // "CALM 30 Pack" or "2 × CALM 30 Pack, BLISS 30 Pack" — one entry per
  // flavour and size, however many lines it is spread over.
  function freeSummary(state) {
    var lines = state.gifts.length ? state.gifts : state.freed;
    var counts = {};
    var order = [];
    lines.forEach(function (line) {
      var name = lineName(line);
      if (!counts[name]) { counts[name] = 0; order.push(name); }
      counts[name] += state.gifts.length ? line.quantity : freedUnits(line);
    });
    return order.map(function (name) {
      return (counts[name] > 1 ? counts[name] + ' × ' : '') + name;
    }).join(', ');
  }

  function renderPicker(el, state) {
    var size = state.minSize || 15;
    el.setAttribute('data-state', 'pick');
    el.innerHTML = '';
    var title = state.missing === state.wanted
      ? (state.wanted > 1 ? copy.unlockedMany : copy.unlocked) || 'You unlocked a free %size% Pack'
      : copy.pickMore || 'Pick %n% more free %size% Pack';
    el.appendChild(paragraph('title', fill(title, { size: size, n: state.missing })));
    el.appendChild(paragraph('subtitle', copy.pick || 'Pick your flavour and we add it for free'));
    var grid = document.createElement('div');
    grid.className = BLOCK_CLASS + '__grid';
    Object.keys(products).forEach(function (id) {
      grid.appendChild(tile(id, products[id], size));
    });
    el.appendChild(grid);
  }

  // Every free pack the cart has earned is in place. With a bigger tier still
  // ahead, lead with that ("add 1 more pack and get 2 free"); at the top tier,
  // say what is free.
  function renderSettled(el, state) {
    var upgrade = state.next;
    el.setAttribute('data-state', upgrade ? 'upgrade' : 'chosen');
    el.innerHTML = '';
    var free = freeSummary(state);
    var summary = fill(state.giftUnits + state.freedPaidUnits > 1
      ? copy.chosenMany || 'Your free packs: %title%'
      : copy.chosen || 'Your free pack: %title%', { title: free });

    if (upgrade) {
      el.appendChild(paragraph('title', fill(copy.upgrade || 'Add %n% more pack and get %free% packs free', {
        n: upgrade.paid - state.units, free: upgrade.free
      })));
    }

    var row = document.createElement('div');
    row.className = BLOCK_CLASS + '__row';
    row.appendChild(paragraph(upgrade ? 'note' : 'title', summary));
    if (state.gifts.length) row.appendChild(button('change', copy.change || 'Change', 'data-b2g1-change'));
    el.appendChild(row);
  }

  function button(className, text, attribute) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = BLOCK_CLASS + '__' + className;
    b.textContent = text;
    b.setAttribute(attribute, '');
    return b;
  }

  // Change mode: pick the full set again; the current gifts stay until it is done.
  function renderChange(el, state) {
    var size = state.minSize || 15;
    var left = state.wanted - changing.length;
    el.setAttribute('data-state', 'pick');
    el.innerHTML = '';
    el.appendChild(paragraph('title', fill(left === state.wanted
      ? (state.wanted > 1 ? copy.changeMany : copy.changeOne) || 'Pick your free %size% Pack'
      : copy.pickMore || 'Pick %n% more free %size% Pack', { size: size, n: left })));
    var row = document.createElement('div');
    row.className = BLOCK_CLASS + '__row';
    row.appendChild(paragraph('subtitle', copy.pick || 'Pick your flavour and we add it for free'));
    row.appendChild(button('change', copy.cancel || 'Cancel', 'data-b2g1-cancel'));
    el.appendChild(row);
    var grid = document.createElement('div');
    grid.className = BLOCK_CLASS + '__grid';
    Object.keys(products).forEach(function (id) {
      grid.appendChild(tile(id, products[id], size));
    });
    el.appendChild(grid);
  }

  function renderNudge(el, state) {
    el.setAttribute('data-state', 'nudge');
    el.innerHTML = '';
    el.appendChild(paragraph('title', fill(copy.nudge || 'Add %n% more pack and pick a third one free', {
      n: tiers[0].paid - state.units
    })));
  }

  function render() {
    if (!cart) return;
    var state = read(cart);
    var showNudge = config.showNudge && state.units > 0 && tiers.length && state.units < tiers[0].paid;

    if (!state.wanted && !showNudge) { removeBlock(); return; }

    var el = block();
    if (!el) return;
    el.classList.toggle('is-busy', busy);
    if (busy) return;

    if (changing && (!state.wanted || !state.gifts.length)) changing = null;

    // Rebuild only when what the block shows would change. Rebuilding on every
    // mutation replaces the buttons under the shopper's pointer, and a press
    // that starts on one node and ends on its replacement is not a click.
    var mode = changing ? 'change' : !state.wanted ? 'nudge' : state.missing ? 'pick' : 'settled';
    var signature = [mode, state.minSize || 15, state.units, state.wanted, state.missing,
      changing ? changing.length : '', mode === 'settled' ? freeSummary(state) : ''].join(':');
    if (el.getAttribute('data-signature') === signature) return;
    el.setAttribute('data-signature', signature);

    if (mode === 'change') renderChange(el, state);
    else if (mode === 'nudge') renderNudge(el, state);
    else if (mode === 'pick') renderPicker(el, state);
    else renderSettled(el, state);
  }

  function rerender() {
    var el = inDrawer('.' + BLOCK_CLASS);
    if (el) el.removeAttribute('data-signature');
    render();
  }

  // One delegated listener for tiles, Change and Cancel, so a block rebuilt
  // between press and release still gets the click.
  function onPick(event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('.' + BLOCK_CLASS)) return;
    if (target.closest('[data-b2g1-change]')) { if (!busy) { changing = []; rerender(); } return; }
    if (target.closest('[data-b2g1-cancel]')) { changing = null; rerender(); return; }

    var button = target.closest('.' + BLOCK_CLASS + '__tile');
    if (!button || button.disabled || busy) return;
    var variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId) return;

    if (changing) {
      changing.push(variantId);
      var wanted = cart ? read(cart).wanted : 0;
      if (changing.length < wanted) { rerender(); return; }
      var picks = changing.slice(0, wanted);
      changing = null;
      run(function () { return replaceGifts(picks); });
      return;
    }
    run(function () { return addGift(variantId); });
  }

  // Rebuy prints the code ("B2G1-QA-7K3M") on freed lines. Say what it is
  // instead. Idempotent: checks the text before writing.
  function relabel() {
    if (!copy.discountLabel) return;
    var lines = document.querySelectorAll('.rebuy-cart__flyout .rebuy-cart__flyout-item-discount-line, .thh-cart [data-thh-discount-line]');
    for (var i = 0; i < lines.length; i++) {
      var text = (lines[i].textContent || '').trim();
      if (relabelTitles.indexOf(text) !== -1) lines[i].textContent = copy.discountLabel;
    }
  }

  // Mark our gift rows so the stylesheet can tell a gift that is actually free
  // from one still waiting on the discount. The shim's FREE badge is hidden on
  // a pending row rather than promising $0.00 the checkout would not show.
  function decorateRows() {
    if (!cart) return;
    var gifts = read(cart).gifts;
    var rows = document.querySelectorAll('.rebuy-cart__flyout .rebuy-cart__flyout-item.property-key-gift.property-value-buy-2-get-1, .thh-cart .thh-line[data-gift-source="' + SOURCE + '"]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var handle = row.getAttribute('data-product-handle') || '';
      if (!handle) {
        String(row.className || '').split(/\s+/).forEach(function (c) {
          if (c.indexOf('product-') === 0) handle = c.slice('product-'.length);
        });
      }
      // Daily Dose Shots gifts share one handle across flavours, so a row is
      // only "free" once every gift line of its product is.
      var matching = gifts.filter(function (g) { return g.handle === handle; });
      if (!matching.length) { row.removeAttribute('data-b2g1-gift'); continue; }
      var allFree = matching.every(function (g) { return freedUnits(g) >= g.quantity; });
      row.setAttribute('data-b2g1-gift', allFree ? 'free' : 'pending');
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () { scheduled = false; render(); relabel(); decorateRows(); });
  }

  // ---- wiring ----

  function onCart(next) {
    cart = next;
    var state = read(cart);
    var signature = [state.units, state.minSize, state.wanted,
      state.gifts.map(function (g) { return g.key + 'x' + g.quantity + ':' + freedUnits(g); }).join(','),
      state.freed.map(function (f) { return f.key + ':' + freedUnits(f); }).join(',')].join('|');
    if (signature !== lastSignature) {
      lastSignature = signature;
      reconcile(state);
    }
    schedule();
  }

  function start() {
    if (config.enabled === false) return;   // kill switch in snippets/b2g1-cart-config.liquid
    var l = layer();
    if (!l) { log('pick-a-card cart layer missing; picker disabled'); return; }
    if (!Object.keys(products).length) { log('no eligible products configured'); return; }
    if (!tiers.length) { log('no offer tiers configured'); return; }

    // Capture phase: the THH drawer panel stops click propagation (@click.stop).
    document.addEventListener('click', onPick, true);
    l.onCartChange(onCart);
    l.readCart().then(function (c) { if (c) onCart(c); });

    var target = document.body || document.documentElement;
    new MutationObserver(schedule).observe(target, { subtree: true, childList: true });
  }

  window.THHB2G1 = {
    version: 2,
    SOURCE: SOURCE,
    giftProperties: giftProperties,
    read: function () { return cart ? read(cart) : null; },
    pick: function (variantId) { return run(function () { return addGift(variantId); }); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
